/**
 * LessonDeepDive.js — On-demand AI-generated comprehensive lesson panel
 * ──────────────────────────────────────────────────────────────────────
 * User mandate: "Generate detailed, comprehensive lesson content for
 * all modules — explanations, supporting scriptures, and practical
 * applications." (e.g., 'His Real Name', 'Sunrise').
 *
 * Cache-first: hits POST /api/lesson-content/generate. First call takes
 * ~60s (Claude Sonnet); every subsequent call for the same lesson_id +
 * grade tier returns instantly from db.lesson_content_cache.
 */
import { useState, useCallback } from "react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const gradeTierFor = (g) => {
  if (!g) return "6-8";
  const s = String(g).toLowerCase();
  if (["k", "1", "2", "3", "4", "5", "k-2", "3-5"].includes(s)) return "K-5";
  if (["6", "7", "8", "6-8"].includes(s)) return "6-8";
  if (["9", "10", "11", "12", "9-12"].includes(s)) return "9-12";
  if (s.includes("seminary") || s.includes("ordination")) return "Seminary";
  return "6-8";
};

export default function LessonDeepDive({
  lessonId,
  lessonTitle,
  subject = "General Studies",
  gradeLevel,
}) {
  const [expanded, setExpanded] = useState(false);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const fetchDeepDive = useCallback(async () => {
    if (data || loading) return;
    setLoading(true);
    setError("");
    try {
      const token = localStorage.getItem("token");
      const headers = token ? { Authorization: `Bearer ${token}` } : {};

      // Kick off (or hit cache). Backend returns {cached:true, ...payload}
      // when ready, or {status:'generating', key} to poll.
      const { data: res } = await axios.post(
        `${API}/lesson-content/generate`,
        {
          lesson_id: lessonId,
          lesson_title: lessonTitle,
          subject,
          grade_tier: gradeTierFor(gradeLevel),
        },
        { headers, timeout: 30000 }
      );

      if (res?.status === "ready" || res?.cached === true) {
        setData(res);
        return;
      }

      // Poll every 4s (up to ~3 min) for the background task to finish.
      const key = res?.key;
      if (!key) throw new Error("No cache key returned");
      const started = Date.now();
      const MAX_WAIT_MS = 180000;
      const POLL_MS = 4000;
      // eslint-disable-next-line no-constant-condition
      while (true) {
        if (Date.now() - started > MAX_WAIT_MS) {
          throw new Error("Timed out waiting for the study companion to build. Please try again in a moment.");
        }
        await new Promise((r) => setTimeout(r, POLL_MS));
        const { data: poll } = await axios.get(
          `${API}/lesson-content/generate/status/${encodeURIComponent(key)}`,
          { headers, timeout: 15000 }
        );
        if (poll?.status === "ready") {
          setData(poll);
          return;
        }
        if (poll?.status === "error") {
          throw new Error("Still building your study companion — please retry in a minute.");
        }
      }
    } catch (e) {
      // Translate raw upstream errors (Cloudflare HTML, network) to a
      // friendly student-facing message.
      const raw = e?.response?.data?.detail || e?.message || "";
      const isHtml = typeof raw === "string" && raw.includes("<html");
      const isTimeout = e?.code === "ECONNABORTED" || /timeout/i.test(raw);
      const friendly = isHtml || isTimeout
        ? "Still building your study companion — please retry in a minute."
        : (raw || "Couldn't generate deep-dive content");
      setError(friendly);
    } finally {
      setLoading(false);
    }
  }, [lessonId, lessonTitle, subject, gradeLevel, data, loading]);

  const toggle = () => {
    const next = !expanded;
    setExpanded(next);
    if (next) fetchDeepDive();
  };

  return (
    <div
      className="card"
      data-testid="lesson-deep-dive"
      style={{
        background: "#0f0f1a",
        borderLeft: "4px solid #DDA0DD",
        marginTop: 15,
      }}
    >
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          cursor: "pointer",
        }}
        onClick={toggle}
        data-testid="deep-dive-toggle"
      >
        <div>
          <h3 style={{ color: "#DDA0DD", margin: "0 0 4px 0" }}>
            📚 Deep Dive: Scriptures, Vocabulary &amp; Application
          </h3>
          <p style={{ color: "#888", fontSize: "0.8rem", margin: 0 }}>
            AI-generated comprehensive companion for this lesson — cached after first load.
          </p>
        </div>
        <span style={{ color: "#DDA0DD", fontSize: "1.3rem" }}>
          {expanded ? "▲" : "▼"}
        </span>
      </div>

      {expanded && (
        <div style={{ marginTop: 16 }} data-testid="deep-dive-body">
          {loading && (
            <div style={{ color: "#aaa", padding: "20px 0" }}>
              <div className="spinner" style={{ margin: "0 auto 12px" }}></div>
              Generating comprehensive study companion in the background…
              first-time build takes about a minute; future visits load instantly.
            </div>
          )}
          {error && !loading && (
            <div className="alert alert-error" data-testid="deep-dive-error">
              {error}
              <button
                onClick={fetchDeepDive}
                style={{
                  marginLeft: 10,
                  background: "transparent",
                  color: "#FFD700",
                  border: "1px solid #FFD70080",
                  padding: "4px 10px",
                  borderRadius: 6,
                  cursor: "pointer",
                }}
              >
                Retry
              </button>
            </div>
          )}
          {data && !loading && (
            <div>
              {data.summary && (
                <div
                  style={{
                    background: "#1a1a2e",
                    padding: 12,
                    borderRadius: 8,
                    marginBottom: 14,
                    color: "#eee",
                    fontStyle: "italic",
                  }}
                >
                  {data.summary}
                </div>
              )}

              {Array.isArray(data.sections) &&
                data.sections.map((s, i) => (
                  <div
                    key={i}
                    data-testid={`deep-dive-section-${i}`}
                    style={{
                      marginBottom: 18,
                      paddingBottom: 14,
                      borderBottom: "1px solid #222",
                    }}
                  >
                    <h4 style={{ color: "#FFD700", margin: "0 0 8px 0" }}>
                      {s.heading}
                    </h4>
                    <div
                      style={{
                        color: "#ddd",
                        whiteSpace: "pre-wrap",
                        lineHeight: 1.55,
                        fontSize: "0.92rem",
                      }}
                    >
                      {s.body}
                    </div>
                    {Array.isArray(s.scriptures) && s.scriptures.length > 0 && (
                      <div style={{ marginTop: 10 }}>
                        <div
                          style={{
                            color: "#87CEEB",
                            fontSize: "0.78rem",
                            fontWeight: "bold",
                            marginBottom: 4,
                          }}
                        >
                          📖 Supporting Scriptures
                        </div>
                        <ul style={{ margin: 0, paddingLeft: 20, color: "#bbb", fontSize: "0.85rem" }}>
                          {s.scriptures.map((scr, j) => (
                            <li key={j}>{scr}</li>
                          ))}
                        </ul>
                      </div>
                    )}
                    {s.application && (
                      <div
                        style={{
                          marginTop: 10,
                          padding: "8px 12px",
                          background: "#1a2a1a",
                          borderLeft: "3px solid #90EE90",
                          color: "#c8e8c8",
                          fontSize: "0.85rem",
                        }}
                      >
                        <strong style={{ color: "#90EE90" }}>Practical Application:</strong>{" "}
                        {s.application}
                      </div>
                    )}
                  </div>
                ))}

              {Array.isArray(data.vocabulary) && data.vocabulary.length > 0 && (
                <div style={{ marginBottom: 16 }}>
                  <h4 style={{ color: "#FFA500", margin: "0 0 8px 0" }}>Vocabulary</h4>
                  <ul style={{ margin: 0, paddingLeft: 20, color: "#ddd", fontSize: "0.88rem" }}>
                    {data.vocabulary.map((v, i) => (
                      <li key={i} style={{ marginBottom: 4 }}>
                        <strong style={{ color: "#FFD700" }}>{v.term}:</strong>{" "}
                        {v.definition}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {Array.isArray(data.discussion_questions) &&
                data.discussion_questions.length > 0 && (
                  <div style={{ marginBottom: 16 }}>
                    <h4 style={{ color: "#87CEEB", margin: "0 0 8px 0" }}>
                      Discussion Questions
                    </h4>
                    <ol style={{ margin: 0, paddingLeft: 22, color: "#ddd", fontSize: "0.88rem" }}>
                      {data.discussion_questions.map((q, i) => (
                        <li key={i} style={{ marginBottom: 4 }}>
                          {q}
                        </li>
                      ))}
                    </ol>
                  </div>
                )}

              {data.zion_pillar_link && (
                <div
                  style={{
                    padding: "10px 14px",
                    background: "#2a1a3a",
                    borderRadius: 8,
                    color: "#DDA0DD",
                    fontSize: "0.85rem",
                    fontStyle: "italic",
                  }}
                >
                  🏛️ <strong>Zion Pillar Link:</strong> {data.zion_pillar_link}
                </div>
              )}

              {data.cached === false && (
                <div style={{ color: "#666", fontSize: "0.7rem", marginTop: 10, textAlign: "right" }}>
                  Freshly generated · cached for all future students
                </div>
              )}
              {data.cached === true && (
                <div style={{ color: "#666", fontSize: "0.7rem", marginTop: 10, textAlign: "right" }}>
                  Loaded from cache · instant
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
